import React, { useState, useEffect, useRef } from 'react';
import { Navbar, Container, Button, OverlayTrigger, Tooltip, Dropdown } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import { apiClient, API_HOST_BASE, getImageUrl } from '../utils/apiUtils';
import { io } from 'socket.io-client';
import './Navbar.css';

const NavigationBar = () => {
  const { user, token, logoutWithRedirect } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notifications, setNotifications] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => document.body.classList.contains('sidebar-collapsed'));
  const socketRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);


  // Initial load + fallback polling for notifications & conversations
  useEffect(() => {
    const loadNotifs = async () => {
      try {
        console.log('Navbar: Loading notifications, token present:', localStorage.getItem('token') ? 'yes' : 'no');
        const { data } = await apiClient.get('/notifications');
        setNotifications(data);
      } catch (e) {
        console.error('Navbar: Failed to load notifications:', e);
        // ignore
      }
    };

    const loadConversations = async () => {
      try {
        console.log('Navbar: Loading conversations, token present:', localStorage.getItem('token') ? 'yes' : 'no');
        const { data } = await apiClient.get('/conversations');
        setConversations(data);
      } catch (e) {
        console.error('Navbar: Failed to load conversations:', e);
        // ignore
      }
    };

    if (user) {
      console.log('Navbar: User present, loading notifications and conversations');
      loadNotifs();
      loadConversations();

      // Keep a light polling fallback in case socket connection fails
      const interval = setInterval(() => {
        loadNotifs();
        loadConversations();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Real-time notifications via Socket.IO
  useEffect(() => {
    if (!user || !token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    // Avoid creating multiple sockets
    if (socketRef.current) return;

    const newSocket = io(API_HOST_BASE, {
      auth: { token },
      transports: ['websocket', 'polling'],
      // 60s so connection survives Render Free tier cold start (~50s)
      timeout: 60000
    });

    newSocket.on('connect', () => {
      console.log('Navbar socket connected for notifications, id:', newSocket.id);
    });

    newSocket.on('notification', (notification) => {
      console.log('Navbar received real-time notification:', notification);
      setNotifications((prev) => [notification, ...prev]);
    });

    newSocket.on('connect_error', (err) => {
      console.error('Navbar socket connection error:', err.message);
    });

    socketRef.current = newSocket;

    return () => {
      newSocket.removeAllListeners();
      newSocket.disconnect();
      socketRef.current = null;
    };
  }, [user, token]);

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await apiClient.post('/notifications/mark-all-read');
      setNotifications(prev => 
        prev.map(n => ({ ...n, status: 'Read' }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Mark individual notification as read
  const markAsRead = async (notifId) => {
    try {
      await apiClient.post(`/notifications/${notifId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notif_id === notifId ? { ...n, status: 'Read' } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all conversations as read
  const markAllConversationsAsRead = async () => {
    try {
      const unreadConversations = conversations.filter(c => c.unread_count > 0);
      await Promise.all(
        unreadConversations.map(conv => 
          apiClient.post(`/conversations/${conv.conversation_id}/read`)
        )
      );
      setConversations(prev => 
        prev.map(conv => ({ ...conv, unread_count: 0 }))
      );
    } catch (error) {
      console.error('Error marking all conversations as read:', error);
    }
  };

  const handleLogout = () => {
    logoutWithRedirect(navigate);
  };


  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSectionTitle = (pathname) => {
    if (!pathname) return '';
    const map = [
      { path: '/dashboard', label: 'Dashboard' },
      { path: '/plan', label: 'Plan' },
      { path: '/projects', label: 'Projects' },
      { path: '/reports', label: 'Reports' },
      { path: '/documents', label: 'Documents' },
      { path: '/monitoring', label: 'Monitoring' },
      { path: '/user-management', label: 'Users' },
      { path: '/logs', label: 'Logs' },
      { path: '/conversations', label: 'Messages' },
      { path: '/calendar', label: 'Calendar' },
      { path: '/notifications', label: 'Notifications' },
      { path: '/profile', label: 'Account' }
    ];
    const found = map.find(m => pathname === m.path || pathname.startsWith(m.path + '/'));
    return found ? found.label : '';
  };
  const sectionTitle = getSectionTitle(location.pathname);

  return (
    <Navbar bg="white" variant="light" expand="lg" className="custom-navbar">
      <Container fluid>
        {/* Left controls: burger + brand text */}
        <div className="d-flex align-items-center me-2">
          <Button
            variant="link"
            className="icon-button me-2"
            aria-label="Toggle sidebar"
            onClick={() => {
              document.body.classList.toggle('sidebar-collapsed');
              setSidebarCollapsed(document.body.classList.contains('sidebar-collapsed'));
              // Notify layout to recompute widths
              window.dispatchEvent(new Event('sidebar-toggle'));
            }}
          >
            <i className="bi bi-list"></i>
          </Button>
          <div className="d-flex align-items-center">
            {sectionTitle && (
              <span className="ms-2 fw-semibold">{sectionTitle}</span>
            )}
          </div>
        </div>

        {/* Right side content */}
        <div className="navbar-right-content">
          {/* Quick-access icons to the LEFT of date/time */}
          <div className="navbar-icons">
            <Button as={Link} to="/conversations" variant="link" className="icon-button position-relative" aria-label="Messages">
              <i className="bi bi-chat-dots"></i>
              {conversations.some(c => c.unread_count > 0) && (
                <span className="position-absolute top-0 start-100 translate-middle badge bg-danger border border-light rounded-pill" style={{ fontSize: '0.7rem', minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {conversations.reduce((total, c) => total + c.unread_count, 0)}
                </span>
              )}
            </Button>
            <Button as={Link} to="/calendar" variant="link" className="icon-button" aria-label="Calendar">
              <i className="bi bi-calendar3"></i>
            </Button>
            <Dropdown align="end">
              <Dropdown.Toggle as={Button} variant="link" className="icon-button position-relative no-caret" aria-label="Notifications">
                <i className="bi bi-bell"></i>
                {notifications.some(n => n.status === 'Unread') && (
                  <span className="position-absolute top-0 start-100 translate-middle badge bg-danger border border-light rounded-pill" style={{ fontSize: '0.7rem', minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {notifications.filter(n => n.status === 'Unread').length}
                  </span>
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ minWidth: 360, maxWidth: 360, width: 360 }}>
                <div className="d-flex justify-content-between align-items-center px-3 py-2">
                  <div className="fw-semibold">Notifications</div>
                  {notifications.some(n => n.status === 'Unread') && (
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="mark-all-read-tooltip">Mark all as read</Tooltip>}
                    >
                      <Button
                        variant="link"
                        size="sm"
                        className="p-1 text-muted"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (typeof markAllAsRead === 'function') {
                            markAllAsRead();
                          } else {
                            console.error('markAllAsRead function not found');
                          }
                        }}
                        style={{ fontSize: '0.8rem' }}
                      >
                        <i className="bi bi-check2-all"></i>
                      </Button>
                    </OverlayTrigger>
                  )}
                </div>
                {notifications.length === 0 && (
                  <Dropdown.ItemText className="text-muted">No notifications</Dropdown.ItemText>
                )}
                {notifications.slice(0, 5).map((n) => (
                  <Dropdown.Item 
                    key={n.notif_id} 
                    onClick={() => {
                      // Mark as read if unread
                      if (n.status === 'Unread') {
                        markAsRead(n.notif_id);
                      }
                      // Navigate to the link
                      navigate(n.link || '/projects');
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex flex-column">
                      <span 
                        className={n.status === 'Unread' ? 'fw-semibold' : ''}
                        style={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          maxWidth: '320px'
                        }}
                        title={n.message}
                      >
                        {n.message}
                      </span>
                      <small className="text-muted">{new Date(n.created_at).toLocaleString()}</small>
                    </div>
                  </Dropdown.Item>
                ))}
                {notifications.length > 5 && (
                  <Dropdown.ItemText className="text-muted text-center small">
                    ... and {notifications.length - 5} more
                  </Dropdown.ItemText>
                )}
                {notifications.length > 0 && <Dropdown.Divider />}
                {notifications.length > 0 && (
                  <Dropdown.Item as={Link} to="/notifications" className="text-center fw-semibold text-primary">
                    <i className="bi bi-eye me-2"></i>
                    View All Notifications
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Date and time */}
          <div className="datetime-section">
            <div className="date-text">{formatDate(currentTime)}</div>
            <div className="time-text">{formatTime(currentTime)}</div>
          </div>

          {/* User dropdown placed next to date/time */}
          {user && (
            <Dropdown align="end">
              <Dropdown.Toggle as={Button} variant="link" className="icon-button d-flex align-items-center">
                {user?.profile_photo ? (
                  <img
                    src={getImageUrl(user.profile_photo, 'profile-photos')}
                    alt="Profile"
                    className="me-2"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid #fff'
                    }}
                    onError={(e) => {
                      console.error('Navbar profile image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'inline-block';
                    }}
                  />
                ) : null}
                <span
                  className="me-2"
                  style={{
                    display: user?.profile_photo ? 'none' : 'inline-block',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #334155, #0ea5e9)'
                  }}
                ></span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Header>
                  <div className="text-center">
                    <div className="fw-bold">{user.fullname}</div>
                    <small className="text-muted">{user.role}</small>
                  </div>
                </Dropdown.Header>
                <Dropdown.Item as={Link} to="/profile">
                  <i className="bi bi-person me-2"></i>
                  Account
                </Dropdown.Item>
                <Dropdown.Item onClick={() => logoutWithRedirect(navigate)} className="text-danger">
                  <i className="bi bi-box-arrow-right me-2"></i>
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </Container>
    </Navbar>
  );
};

export default NavigationBar;
